import React, { useState, useMemo, useRef, useEffect } from 'react';
import Box from 'react-styled-box';
import { connect, useDispatch } from 'react-redux';
import {bindActionCreators} from 'redux';
import { Field, change, reset, reduxForm, formValueSelector, SubmissionError } from 'redux-form';
import _pick from 'lodash/pick';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _size from 'lodash/size';
import store from 'store';
import usePrevious from 'UsePrevious.js';
import useToggle from 'UseToggle.js'
import { fetchNewCustomerDataByMyId } from 'customerDataByMyIdActions.js';
import { preregisterUser, registerUser, registerUserByMyId, verificationCode, smsCodeVerifyWithAttempts } from 'authActions.js';
import { loadPublicRegConsents, getMyIdStatus } from 'customerActions.js';
import { addIncomeAccessData, setIncomeAccessIframe } from 'integrationActions.js';
import { fetchPromoCodeSetting } from 'topMenuActions.js';
import { translation, triggerNativeEvent, redirect} from 'utilsHelper.js';
import { getPromoCookie, setGoogleDataLayerCookie, getBtagCookie } from 'cookiesHelper.js';
import { prepareUserData, renderField, prepareRegistrationPayload } from 'registerHelper.js'
import { substractYears} from 'datesHelper.js';
import { STEPS_CONFIG, HEADERS } from 'registerConsts.js'
import ModalOpacity from 'ModalOpacity.js';
import RegisterWizardFormStep0 from 'RegisterWizardFormStep0.js';
import RegisterWizardFormStep1 from 'RegisterWizardFormStep1.js';
import RegisterWizardFormStep2 from 'RegisterWizardFormStep2.js';
import RegisterFormByMyIdStep1 from 'RegisterFormByMyIdStep1.js';
import RegisterFormByMyIdStep2 from 'RegisterFormByMyIdStep2.js';
import RegisterFormByMyIdStep3 from 'RegisterFormByMyIdStep3.js';
import CmsPage from 'CmsPage.js';
import BellIcon from 'bell-icon.svg';
import S from 'StyledRegisterWizardForm.js';

const asyncValidate = async (values, dispatch, props, field) => {
    const { step, registrationMethod } = props;

    const isSmsStep = (registrationMethod === 'bank' && step === 3) || (registrationMethod === 'form' && step === 2);

    if (isSmsStep && (field === 'smsCode' || !field) && values.smsCode) {
        try {
            await dispatch(smsCodeVerifyWithAttempts('smsCode', values.smsCode, values.mobile));
        } catch (err) {
            throw { smsCode: [translation('account_registerForm_verificationCode_wrongSMSCode')] };
        }
    }
};

const RegisterWizardForm = (props) => {
    const {
        initialValues,
        smsVerificationCode, 
        personalIdentifier, 
        selectAll, 
        fetchPromoCodeSetting, 
        isPromoCodeVisible,
        marketingConsent, 
        loadPublicRegConsents,
        isMyIdActive,
        getMyIdStatus,
        fetchNewCustomerDataByMyId, 
        submitting,
        handleSubmit,
        userData,
        mobile,
        verificationCode,
        registerUserByMyId
    } = props;

    const isBankPath = window.location.pathname.includes('registerByMyId');

    const [affiliateParam, setAffiliateParam] = useState(null);
    const [registrationMethod, setRegistrationMethod] = useState(null);
    const [step, setNextStep] = useState(0);
    const [registrationConsents, setRegConsents] = useState([]);
    const [pendingValues, setPendingValues] = useState(null);
    const [wasMarketingModalShown, setWasMarketingModalShown] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(0);
    const [showSmsModal, toggleSmsModal] = useToggle(false);
    const [showConfirmationModal, toggleConfirmationModal] = useToggle(false);
    const [showMarketingConsentModal, toggleMarketingConsentModal] = useToggle(false);
    const prevStep = usePrevious(step);
    const prevFirstStepValues = usePrevious({smsVerificationCode, personalIdentifier});
    const incomeAccessIframeRef = useRef();
    const dispatch = useDispatch();
    const customerRegStepTwoTag = 'CUSTOMER_REGISTRATION_STEP_2';

    const toggleRegistrationTag = (addTag, tagName) => {
        const loc = window.location;
        if (addTag) {
            history.replaceState(null, null, `#${tagName}`);
        } else {
            if ('pushState' in history) {
                history.replaceState({}, document.title, loc.href.substr(0, loc.href.length - loc.hash.length));
            }
        }
    };

    useEffect(() => {
        toggleRegistrationTag(true, 'registerStart');
    }, []);

    useEffect(() => {
        getMyIdStatus();
        fetchPromoCodeSetting();
        getPublicRegConsents().then(data => setRegConsents(data)); 
    }, []);

    useEffect(() => {
        let timer;
        if (resendCountdown > 0) {
            timer = setInterval(() => {
                setResendCountdown(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [resendCountdown]);

    useEffect(() => {
        if (step === 0) {
            setWasMarketingModalShown(false);
        }
    }, [step, registrationMethod]);

    useEffect(() => {
        const {dispatch} = store;
        if (step == 1 && step != prevStep) {
            setTimeout(() => {
                dispatch(change('registerWizardForm', 'password', ''))
            }, 500);
        } else if (step == 2 && step != prevStep) {
            if (prevFirstStepValues.personalIdentifier){
                triggerNativeEvent('[name="personalIdentifier"]', 'blur');
            }
            if (prevFirstStepValues.smsVerificationCode){
                triggerNativeEvent('[name="verificationCode"]', 'blur');
            }
        }
    }, [step]);

    //read btag query param and call CMS place with banner based on it
    useEffect(() => {
        const btagParam = getBtagCookie();
        let firstPartBtagParam = btagParam?.split("b")[0];
        firstPartBtagParam = firstPartBtagParam?.replace('_', '');
        firstPartBtagParam = firstPartBtagParam ? firstPartBtagParam : 'default';
        setAffiliateParam(firstPartBtagParam);
    }, []);

    useEffect(() => {
        if (isBankPath) { 
            const path = window.location.pathname;
            const tokenFromPath = path.split('registerByMyId/')[1]?.split('#')[0]?.split('?')[0];
            const params = new URLSearchParams(window.location.search);
            const myIdToken = params.get('myIdToken');
            const finalToken = myIdToken || tokenFromPath;

            if (finalToken) {
                setRegistrationMethod('bank');
                setNextStep(1);
                fetchNewCustomerDataByMyId(finalToken);
            }
        }
    }, [isBankPath]);

    const memoizedInitialValues = useMemo(() => {
        const base = userData || {};
        return {
            country: base.country || 'PL',
            ...base,
            bonus: true
        };
    }, [userData]);

    const startCountdown = () => setResendCountdown(60);

    const handleResendSms = async () => {
        if (resendCountdown === 0 && mobile) {
            try {
                await verificationCode({ mobile });
                startCountdown();
            } catch (error) {
                throw error;
            }
        }
    };

    const handleMethodSelect = (method) => {
        setRegistrationMethod(method);
        setNextStep(1);
    };

    const handleBankRedirect = () => {
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        
        const apiBase = isLocalhost 
            ? 'https://testm.fuksiarz.pl' 
            : window.location.origin;

        const returnUrl = encodeURIComponent(`${window.location.origin}/registerByMyId`);
        const initializeUrl = `${apiBase}/rest/customer/identity-resolver/my-id/initialize?returnUrl=${returnUrl}`;
        window.location.href = initializeUrl;
    };

    const getPublicRegConsents = async() => {
        try {
            const data = await loadPublicRegConsents(customerRegStepTwoTag);
            return data.CUSTOMER_REGISTRATION_STEP_2 || [];
        } catch (error) {
            throw error;
        }
    };

    const registerFirstStep = async (values) => {

        const {dispatch} = store;
        const pickedValues = ['email', 'marketingConsent', 'mobile', 'previousMobileNumber'];
        const registerData = _pick(values, pickedValues);
        const marketingConsentInner = _get(registerData, ['marketingConsent']);
        const previousMobileNumber = _get(registerData, ['previousMobileNumber']);
        try {

            if (marketingConsentInner == true) {
                await dispatch(preregisterUser(registerData));
            }

            const mobile = _pick(values, ['mobile']);
            if (mobile.mobile != previousMobileNumber) {
                await dispatch(change("registerForm","previousMobileNumber",mobile.mobile));
                dispatch(verificationCode(mobile));
            }

            setNextStep((prevStep) => prevStep + 1);

        } catch (error) {
            throw new SubmissionError({_error: error});
        } 
        
    };

    const handleSubmitOnClick = async (values) => {
        setPendingValues(values);

        if (values.marketingConsent !== true && !wasMarketingModalShown) {
            setWasMarketingModalShown(true);
            toggleMarketingConsentModal();
        }

        await registerFirstStep(values);
    }

    const handleButtonClick = async (marketingConsent = false) => {
        const { dispatch } = store;

        await dispatch(change("registerWizardForm", "marketingConsent", marketingConsent)); 

        toggleMarketingConsentModal(); 

        if (registrationMethod === 'bank' && step === 3) {
            processStep3Flow(pendingValues, marketingConsent);
        } else {
            await handleSubmit(registerFirstStep)();
        }
    }

    const registerOnSubmit = async (postData) => {
        try {
            const newUser = await dispatch(registerUser(postData));
            return newUser;
        } catch (error) {
            throw error;
        }
    };

    const registerSecondStep = async (values) => {
        try {

            let pickedValues = [
                'firstName',
                'lastName',
                'nationality',
                'password',
                // 'isBonus',
                'email',
                'mobile',
                // 'marketingConsent',
                'verificationCode'
            ];

            const nationalityPL = _get(values, ['nationality']);
            if (nationalityPL == 'PL') {
                pickedValues = [...pickedValues, 'personalIdentifier'];
            } else {
                pickedValues = [...pickedValues, 'dateOfBirth', 'address', 'postalCode'];
            }

            const fieldPromoCode = _get(values, ['promoCode']);

            if (fieldPromoCode){
                pickedValues = [...pickedValues, 'promoCode'];
            }

            let registerData = _pick(values, pickedValues);
            if ('personalIdentifier' in registerData) {
                registerData['personalIdentificationNumber'] = registerData['personalIdentifier'];
                delete registerData['personalIdentifier'];
            }


            const emailVerification = _get(values, ['emailVerification']);
            if(emailVerification){
                registerData['verificationCode'] = null;
            }

            // opis działania w tasku 80019 - działanie na prośbę klienta przeczy wszelkiej logice
            const promoCode = getPromoCookie();
            if(!isPromoCodeVisible && promoCode){
                registerData['promoCode'] = promoCode;
            } else if (!promoCode && !fieldPromoCode) {
                registerData['promoCode'] = app.config.defaultPromoCode;
            }

            registerData.countryCode = process.env.MAIN_COUNTRY_CODE;
            registerData.currencyCode = process.env.MAIN_CURRENCY_CODE;

            registerData = addIncomeAccessData(registerData);

            const btag = registerData.affiliateId;
            if (btag){ 
                registerData['btag'] = btag;
                registerData = _.omit(registerData, 'affiliateId');
            }

            const offerContainers = [];
            for(let i in registrationConsents){
              offerContainers.push({
                "container": "OFFERS", "accepted": _get(values, ['isBonus']), "definitionId": registrationConsents[i].definitionId
              })
            }

            registerData['consents'] = [
                { "container": "LOTTERY_PARTICIPATION", "accepted": false },
                { "container": "REGULATIONS", "accepted": true },
                ...offerContainers
            ];

            const marketingConsent = _get(values, ['marketingConsent']);
            if (marketingConsent) {
                registerData['consents'].push(
                    { "container": "EMAIL", "accepted": marketingConsent },
                    { "container": "SMS", "accepted": marketingConsent },
                )
            } else if (emailVerification) {
                registerData['consents'].push(
                    { "container": "EMAIL", "accepted": true },
                    { "container": "SMS", "accepted": false },
                )
            } else {
                registerData['consents'].push(
                    { "container": "EMAIL", "accepted": false },
                    { "container": "SMS", "accepted": true },
                )
            }

            //temporary solution for my affiliate token
            const myAffiliateToken = Cookies.get('myAffiliateToken');
            if (myAffiliateToken) registerData['affiliateToken'] = myAffiliateToken;

            const customerId = await registerOnSubmit(registerData);
            await setIncomeAccessIframe(customerId, incomeAccessIframeRef);

            if (customerId) {
                 setGoogleDataLayerCookie(customerId);
            }

            toggleRegistrationTag(true, 'registerEnd');

            if(!emailVerification){
                const loginData = _pick(values, ['password']);
                loginData['login'] = _get(_pick(values, ['email']), ['email']);
                await app.component.LoginForm.loginRequest(loginData);
                resetWizardForm()
            } else {
                toggleConfirmationModal();
            }
        } catch (error) {
            throw new SubmissionError({ _error: error });
        }
    };

    const initialValuesStepTwo = useMemo(() => {
        const date = substractYears(new Date(), 18);
        let data = {
            isBonus: true,
            dayOfBirth: date.getDate(),
            monthOfBirth: date.getMonth(),
            yearOfBirth: date.getFullYear(),
            nationality: process.env.MAIN_COUNTRY_CODE
        };

        const promo = getPromoCookie();
        promo? data['promoCode'] = promo : data['promoCode'] = app.config.defaultPromoCode

        return data;
    }, []);

    const resetWizardForm = () => {
        dispatch(reset("registerForm"))
        redirect('/');
    }

    const onConfirmationCloseClick = () => {
        toggleConfirmationModal()
        resetWizardForm()
    }

    const onStep3Submit = async (values) => {
        setPendingValues(values);

        if (values.marketingConsent === true || wasMarketingModalShown) {
            processStep3Flow(values, values.marketingConsent || false);
        } else {
            setWasMarketingModalShown(true);
            toggleMarketingConsentModal();
        }
    };

    const processStep3Flow = async (values, marketingAccepted) => {
        if (marketingAccepted) {
            try {
                await dispatch(preregisterUser({
                    email: values.email,
                    marketingConsent: true,
                    mobile: values.mobile
                }));
            } catch (e) { console.error(e); }
        }

        if (!values.emailActivation) {
            try {
                if(values.mobile) {
                    verificationCode({ 'mobile': values.mobile });
                    startCountdown();
                }
                toggleSmsModal(true);
            } catch (error) { console.error(error); }
        } else {
            handleFinalRegistration(values);
        }
    };

    const handleFinalRegistration = async (values) => {
        try {
            const myAffiliateToken = getBtagCookie();
            const btag = getBtagCookie();
            const clickId = btag;
            const promoCode = getPromoCookie() || app.config.defaultPromoCode;

            const payload = prepareRegistrationPayload(values, myAffiliateToken, btag, clickId, promoCode);

            const customerId = await registerUserByMyId(payload);
            await setIncomeAccessIframe(customerId, incomeAccessIframeRef);

            if (customerId) {
                setGoogleDataLayerCookie(customerId);
            }

            toggleRegistrationTag(true, 'registerEnd');
            toggleSmsModal(false);

            if (!values.emailActivation) {
                const loginData = {'login': values.email, 'password': values.password};
                await app.component.LoginForm.loginRequest(loginData);
                resetWizardForm()
            } else {
                toggleConfirmationModal();
            }            
        } catch (error) {
            toggleSmsModal(false);
            throw new SubmissionError({_error: error});
        }
    };

    const isCountryFromBank = !!(userData && userData.country);

    return (
        <S.RegisterWizardForm>
            <ModalOpacity isOpen={showConfirmationModal}
                toggleOpen={toggleConfirmationModal}
                title="Confirmation"
                showHeader={false}
                padding={0}>
                <S.ConfirmationModal>
                    <S.ConfirmationMsg>{translation('account_unloggedPanel_confirmationModal')}</S.ConfirmationMsg>
                    <S.ConfirmationCloseBtn
                        onClick={onConfirmationCloseClick}>{translation('common_ok')}</S.ConfirmationCloseBtn>
                </S.ConfirmationModal>
            </ModalOpacity>

            { affiliateParam && <CmsPage name={`affiliate-${affiliateParam}`} alternativeContent="affiliate-default" isPlaces={true}/> }
            
            {step > 0 && registrationMethod && HEADERS[registrationMethod][step - 1] && (
                <S.RegisterWizardFormTitle> {translation(HEADERS[registrationMethod][step - 1].translation_key)}</S.RegisterWizardFormTitle>
            )}
            
            {step > 0 && registrationMethod && (
                <S.Stepper stepsCount={STEPS_CONFIG[registrationMethod].length}>
                    {STEPS_CONFIG[registrationMethod].map((config, index) => {
                        const stepNum = index + 1;
                        const isCompletedOrActive = step >= stepNum;
                        const isCurrent = step === stepNum;
                        const canGoBack = step > stepNum;

                        return (
                            <S.StepWrapper key={index}>
                                <S.Step 
                                    hasPointer={canGoBack} 
                                    onClick={() => canGoBack && setNextStep(stepNum)} 
                                >
                                    <S.StepCircle isActive={isCompletedOrActive}>
                                        {stepNum}
                                    </S.StepCircle>
                                    <S.StepLabel isActive={isCompletedOrActive} isCurrent={isCurrent}>
                                        {translation(config.label)}
                                    </S.StepLabel>
                                </S.Step>
                            </S.StepWrapper>
                        );
                    })}
                </S.Stepper>
            )}

            {/* MARKETING CONSENT ACCEPTATION MODAL */}
            <ModalOpacity isOpen={showMarketingConsentModal}
                          showHeader={false}
                          showCloseIcon={false}
                          padding={0}
                          >
                <S.Modal>
                    <div className="confirm-modal-content">
                        <S.BellIcon dangerouslySetInnerHTML={{__html: BellIcon}}></S.BellIcon>                     
                        <S.ModalText data-translation="registerForm_marketingConsent_additionalText">{translation('registerForm_marketingConsent_additionalText')}</S.ModalText>                       
                        <div className="buttonsWrapper">
                            <S.ModalButton onClick={()=>{handleButtonClick(true)}} data-translation="registerForm_marketingConsent_buttonAccept">{translation('registerForm_marketingConsent_buttonAccept')}</S.ModalButton>
                            <S.ModalButton cancel onClick={()=>{handleButtonClick(false)}} data-translation="registerForm_marketingConsent_buttonCancel">{translation('registerForm_marketingConsent_buttonCancel')}</S.ModalButton>
                        </div>
                    </div>
                </S.Modal>
            </ModalOpacity>

            {/* SMS VERIFICATION CODE MODAL */}
            <ModalOpacity isOpen={showSmsModal} toggleOpen={toggleSmsModal} showHeader={false} padding={0} preventCloseOnOverlay={true}>
                <S.Dialog>
                    <Box flexDirection="column" alignItems="center">
                        <S.DialogText>{translation('registerForm_smsModalHeader')}</S.DialogText>
                        <Box width="100%" margin="10px 0 20px 0">
                            <Field name="smsCode" component={renderField} S={S} label={translation('registerForm_smsCodePlaceholder')}/>
                        </Box>
                        <Box flexDirection="column" alignItems="center" margin="15px 0 0">
                            <S.ResendText>{translation('registerForm_smsModalText') }</S.ResendText>
                            {resendCountdown > 0 ? (
                                <S.ResendCounter>
                                    {translation('registerForm_resendCodeWait')} {resendCountdown}s
                                </S.ResendCounter>
                            ) : (
                                <S.ResendButton onClick={handleResendSms}>
                                    {translation('registerForm_resendSmsCode')}
                                </S.ResendButton>
                            )}
                        </Box>
                        <Box width="100%">
                            <S.SubmitBtn disabled={submitting} onClick={handleSubmit(handleFinalRegistration)}>
                                {translation('registerForm_submit')}
                            </S.SubmitBtn>
                        </Box>
                    </Box>
                </S.Dialog>
            </ModalOpacity>

            {step === 0 && !isBankPath && (
                <RegisterWizardFormStep0 
                    onFormSelect={() => handleMethodSelect('form')} 
                    onBankSelect={handleBankRedirect}
                    isMyIdActive={isMyIdActive}
                />
            )}

            {registrationMethod === 'bank' && (
                <>
                    {step === 1 && <RegisterFormByMyIdStep1 onSubmit={() => setNextStep(2)} initialValues={memoizedInitialValues}/>}
                    {step === 2 && <RegisterFormByMyIdStep2 onSubmit={() => setNextStep(3)} initialValues={memoizedInitialValues} isCountryLocked={isCountryFromBank}/>}
                    {step === 3 && <RegisterFormByMyIdStep3 onSubmit={(values) => onStep3Submit(values)} initialValues={memoizedInitialValues}/>}
                </>
            )}

            {registrationMethod === 'form' && (
                <>
                    {step === 1 && <RegisterWizardFormStep1 onSubmit={registerFirstStep}/>}
                    {step === 2 && <RegisterWizardFormStep2 initialValues={initialValuesStepTwo} onSubmit={registerSecondStep} isPromoCodeVisible={isPromoCodeVisible} goToPrevPage={() => setNextStep(1)}/>}
                </>
            )}

            <S.HideIframe ref={incomeAccessIframeRef}></S.HideIframe>
        </S.RegisterWizardForm>
    )
};

const selector = formValueSelector('registerWizardForm');
const mapStateToProps = (state) => {
    const {TopMenu:{ isPromoCodeVisible }} = state;
    const isMyIdActive = _get(state, 'Customer.isMyIdActive', false);
    const customerDataByMyId = _get(state, 'CustomerDataByMyId.customerDataByMyId');

    const userData = prepareUserData(customerDataByMyId);

    return {
        personalIdentifier:  selector(state, 'personalIdentifier'),
        smsVerificationCode:  selector(state, 'verificationCode'),
        marketingConsent:  selector(state, 'marketingConsent'),
        isPromoCodeVisible,
        mobile: selector(state, 'mobile'),
        isPromoCodeVisible,
        isMyIdActive,
        customerDataByMyId,
        userData,
    };
};

const mapDispatchToProps = (dispatch) => {
    const bindedActions = {
      fetchPromoCodeSetting,
      loadPublicRegConsents,
      registerUser,
      preregisterUser,
      verificationCode,
      getMyIdStatus,
      fetchNewCustomerDataByMyId,
      registerUserByMyId
    }

    return {
      ...bindActionCreators(bindedActions, dispatch),
      dispatch
    }
};

const connectedWizard = connect(mapStateToProps, mapDispatchToProps)(RegisterWizardForm);

export default reduxForm({
    form: 'registerWizardForm',
    asyncValidate,
    asyncBlurFields: ['smsCode'],
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: false,
    enableReinitialize: true, 
    keepDirtyOnReinitialize: true
})(connectedWizard);